/**
 * Post-Onboarding Results.
 *
 * The first thing an owner sees after "That's mine" or manual entry.
 * Sections:
 *   1. Business snapshot   — what we already know (Google-verified facts only)
 *   2. Health at a glance  — condensed observation summary
 *   3. AI Visibility       — ChatGPT / Gemini / Perplexity comparison
 *   4. Service Offers      — problem→solution routes with placeholder copy
 *
 * Guardrails:
 *   - LLM output is always labelled "What {model} said" — never presented
 *     as fact. Business fields (name, address, phone, review count, …)
 *     come only from Google Places / owner-entered data.
 *   - "Refresh" for the AI snapshot polls until every probe is either
 *     `done` or `error`. Never hangs.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import {
  getOnboardingResults,
  startAiVisibilitySnapshot,
  getAiVisibilitySnapshot,
} from "@/lib/api";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 45; // ~90s ceiling

export default function OnboardingResults() {
  const { businessId } = useParams();
  const navigate = useNavigate();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiState, setAiState] = useState("idle"); // idle | running | done | error
  const [aiSnapshot, setAiSnapshot] = useState(null);
  const pollRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Post-OAuth return handshake — read `?meta=connected|error&reason=...`
  // once, toast the outcome, and strip the query so a refresh doesn't
  // fire the toast again.
  useEffect(() => {
    const status = searchParams.get("meta");
    if (!status) return;
    const reason = searchParams.get("reason") || "";
    if (status === "connected") {
      toast.success("Facebook connected — we can now see real activity on your page.");
    } else {
      toast.error(reason ? `Facebook connect failed: ${reason}` : "Facebook connect didn't complete.");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("meta");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sprint 3I — Website Check card. When the customer-safe state
  // reports `pending`, the auto-pilot scan hasn't persisted yet. Poll
  // gently every 4s up to ~30s so the card resolves to its real state
  // without the owner needing to refresh.
  useEffect(() => {
    if (!results) return;
    const state = results?.websiteCheck?.state;
    if (state !== "pending") return;
    let cancelled = false;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const data = await getOnboardingResults(businessId);
        if (cancelled) return;
        if (data?.websiteCheck?.state && data.websiteCheck.state !== "pending") {
          setResults(data);
          clearInterval(iv);
        } else if (attempts >= 8) {
          // 30s ceiling — stop polling; the card will remain "pending"
          // until the next full page refresh.
          clearInterval(iv);
        }
      } catch (e) {
        // Silent — the pending copy is safe and self-explanatory.
        if (attempts >= 8) clearInterval(iv);
      }
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [results, businessId]);

  // Initial load.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getOnboardingResults(businessId);
        if (!alive) return;
        setResults(data);
        setAiSnapshot(data?.aiSnapshot || null);
        if (data?.aiSnapshot?.status === "complete") setAiState("done");
      } catch (e) {
        toast.error("We couldn't load your results just now.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [businessId]);

  const runAiSnapshot = async () => {
    if (aiState === "running") return;
    setAiState("running");
    try {
      const { snapshotId, snapshot } = await startAiVisibilitySnapshot(businessId);
      if (snapshot?.status === "complete") {
        setAiSnapshot(snapshot);
        setAiState("done");
        return;
      }
      // Poll until the six probes finish (backend runs them synchronously
      // today, so this branch rarely fires — kept for the future async
      // version).
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const snap = await getAiVisibilitySnapshot(snapshotId);
          if (snap?.status === "complete") {
            clearInterval(pollRef.current);
            setAiSnapshot(snap);
            setAiState("done");
          } else if (attempts >= POLL_MAX_ATTEMPTS) {
            clearInterval(pollRef.current);
            setAiState("error");
            toast.error("Your AI snapshot took longer than expected. Try again shortly.");
          }
        } catch (e) {
          clearInterval(pollRef.current);
          setAiState("error");
        }
      }, POLL_INTERVAL_MS);
    } catch (e) {
      setAiState("error");
      toast.error("We couldn't start your AI snapshot. Try again shortly.");
    }
  };

  if (loading) {
    return (
      <AppShell testId="onboarding-results-loading">
        <div className="py-16 text-center" style={{ color: "var(--ta-text-secondary)" }}>
          Pulling together what we already know about you…
        </div>
      </AppShell>
    );
  }

  if (!results?.business) {
    return (
      <AppShell testId="onboarding-results-empty">
        <div className="py-16 text-center">
          <p style={{ color: "var(--ta-text-secondary)" }}>
            We couldn&apos;t find that business. It may not have finished saving yet.
          </p>
          <button
            data-testid="results-go-dashboard"
            onClick={() => navigate("/dashboard")}
            className="mt-6 px-5 py-2 rounded-full text-sm font-medium"
            style={{ background: "var(--ta-primary)", color: "white" }}
          >
            Go to dashboard
          </button>
        </div>
      </AppShell>
    );
  }

  const biz = results.business;

  return (
    <AppShell testId="onboarding-results">
      <div className="max-w-3xl mx-auto pb-24 space-y-8">
        {/* Header — Sprint 3J */}
        <header data-testid="results-header">
          <div
            className="text-xs uppercase tracking-widest font-semibold mb-2"
            style={{ color: "var(--ta-secondary)" }}
            data-testid="results-eyebrow"
          >
            BUSINESS SNAPSHOT
          </div>
          <h1
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]"
            style={{ color: "var(--ta-primary)" }}
            data-testid="results-headline"
          >
            We&apos;ve already started working on {biz.businessName || "your business"}.
          </h1>
          <p
            className="mt-3 text-base sm:text-lg leading-relaxed"
            style={{ color: "var(--ta-text-secondary)" }}
          >
            We&apos;re checking how customers can find, understand, and choose your business. Here&apos;s what we know so far.
          </p>
        </header>

        {/* 1. Business snapshot */}
        <BusinessSnapshot biz={biz} />

        {/* 2. Sprint 3J — "What we found so far" (top findings) */}
        <TopFindingsSection findings={results.topFindings} />

        {/* 3. Health at a glance — Sprint 3K: curated max 5 findings. */}
        <HealthObservations
          items={results.healthItemsCurated || (results.healthItems || []).slice(0, 5)}
          count={results.observationCount}
        />

        {/* 4. Website Check (Sprint 3I) — safe customer view of the
            automated homepage scan performed by auto-pilot. */}
        <WebsiteCheckSection check={results.websiteCheck} />

        {/* 5. AI Visibility */}
        <AiVisibilitySection
          state={aiState}
          snapshot={aiSnapshot}
          onRun={runAiSnapshot}
          businessName={biz.businessName}
          businessId={biz.id || businessId}
        />

        {/* 6. Social Presence — Sprint 3K compact summary. */}
        <SocialPresenceSection report={results.socialPresence} />

        {/* Sprint 3K — Service offers are intentionally NOT rendered
            on the initial Results page. The offer system (backend +
            data) remains fully intact; contextual offers are
            surfaced elsewhere so the "wow moment" stays value-first,
            not sales-first. */}

        {/* 8. Sprint 3J — Your First Monday Brief confirmation */}
        <FirstBriefSection firstBrief={results.firstBrief} />

        {/* Footer — Sprint 3K: completion treatment, not a
            dashboard-first CTA. Owners leave the page believing
            work has already begun and the Monday Brief will come
            to their inbox. */}
        <div className="pt-8 text-center" data-testid="results-completion">
          <h2
            className="font-display text-2xl sm:text-3xl font-semibold tracking-tight"
            style={{ color: "var(--ta-primary)" }}
            data-testid="results-completion-headline"
          >
            You&apos;re all set.
          </h2>
          <p
            className="mt-2 text-sm sm:text-base leading-relaxed"
            style={{ color: "var(--ta-text-secondary)" }}
            data-testid="results-completion-supporting"
          >
            We&apos;ll send your Monday Brief directly to your inbox. No login required.
          </p>
          <div className="mt-5">
            <button
              data-testid="results-secondary-snapshot"
              onClick={() => navigate("/dashboard")}
              className="text-sm underline"
              style={{ color: "var(--ta-primary)" }}
            >
              Explore your business snapshot
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

/* ---------- Business Snapshot (Sprint 3K) ---------- */
const BusinessSnapshot = ({ biz }) => {
  // Sprint 3K — never expose internal enum values (e.g.
  // `automotive_services`). Server-side `tradeLabel` field provides the
  // customer-friendly label. Rows for state / phone / website are
  // omitted entirely when the underlying data is absent — "—" is a
  // trust anti-pattern.
  const rows = [];
  const tradeLabel = biz.tradeLabel || biz.trade;
  if (tradeLabel) rows.push(["Trade", tradeLabel]);
  if (biz.city) rows.push(["City", biz.city]);
  if (biz.state) rows.push(["State", biz.state]);
  if (biz.zipCode) rows.push(["ZIP", biz.zipCode]);
  if (biz.phoneNumber) rows.push(["Phone", biz.phoneNumber]);
  if (biz.websiteUrl) {
    const cleanUrl = biz.websiteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
    rows.push(["Website", cleanUrl]);
  }

  // Sprint 3K — customer-safe copy when Google didn't provide the
  // review data. Missing rating / review-count fields must NEVER be
  // presented as zero / none / not-visible.
  //
  // We reveal a confirmed value only when the underlying number is
  // real (non-null). Otherwise we say "Google didn't provide … in
  // this check." — a factual, calm statement of what we know.
  let ratingLine;
  if (typeof biz.rating === "number") {
    ratingLine = `${biz.rating.toFixed(1)}\u2605`;
  } else {
    ratingLine = "Google didn\u2019t provide rating information in this check.";
  }
  let reviewLine;
  if (typeof biz.reviewCount === "number") {
    reviewLine = `${biz.reviewCount} review${biz.reviewCount === 1 ? "" : "s"}`;
  } else {
    reviewLine = "Google didn\u2019t provide a review count in this check.";
  }
  rows.push(["Google rating", ratingLine]);
  rows.push(["Google reviews", reviewLine]);

  rows.push([
    "Discovery",
    biz.discoverySource === "google_places"
      ? "Verified via Google Business Profile"
      : "Owner-entered",
  ]);

  return (
    <section
      data-testid="results-business-snapshot"
      className="rounded-2xl p-6"
      style={{ background: "var(--ta-surface)", border: "1px solid var(--ta-border)" }}
    >
      <h2
        className="text-lg font-semibold mb-4"
        style={{ color: "var(--ta-primary)" }}
      >
        Business snapshot
      </h2>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
        {rows.map(([k, v]) => (
          <div key={k} className="flex flex-col" data-testid={`snapshot-row-${k.toLowerCase().replace(/\s+/g, "-")}`}>
            <dt
              className="text-xs uppercase tracking-wide"
              style={{ color: "var(--ta-text-secondary)" }}
            >
              {k}
            </dt>
            <dd className="text-sm mt-0.5 break-words" style={{ color: "var(--ta-primary)" }}>
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

/* ---------- Health Observations (Option B: always visible) ---------- */
const HealthObservations = ({ items, count }) => {
  if (!items?.length) {
    return (
      <section
        data-testid="results-health-observations"
        className="rounded-2xl p-6"
        style={{ background: "var(--ta-surface)", border: "1px solid var(--ta-border)" }}
      >
        <h2 className="text-lg font-semibold" style={{ color: "var(--ta-primary)" }}>
          Health at a glance
        </h2>
        <p className="mt-2 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
          Your first observations will appear once we finish reading your listing.
        </p>
      </section>
    );
  }
  const order = { red: 0, amber: 1, green: 2 };
  const sorted = [...items].sort((a, b) => (order[a.tone] ?? 3) - (order[b.tone] ?? 3));
  return (
    <section
      data-testid="results-health-observations"
      className="rounded-2xl p-6"
      style={{ background: "var(--ta-surface)", border: "1px solid var(--ta-border)" }}
    >
      <h2 className="text-lg font-semibold" style={{ color: "var(--ta-primary)" }}>
        Health at a glance
      </h2>
      <p className="mt-1 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
        Every signal we found in your Google Business Profile &mdash; the wins and the gaps.
      </p>
      <ul className="mt-5 space-y-0">
        {sorted.map((it, i) => (
          <HealthObservationRow key={`${it.kind}-${i}`} item={it} />
        ))}
      </ul>
      <p className="mt-4 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
        {count || items.length} observation{items.length === 1 ? "" : "s"} in total. Anything new
        we find between now and Monday will land in your first Monday Brief.
      </p>
    </section>
  );
};

const HealthObservationRow = ({ item }) => {
  const dot =
    item.tone === "green" ? "#1f7a3a" : item.tone === "amber" ? "#c98a15" : "#a4272b";
  return (
    <li
      data-testid={`health-obs-${item.kind}`}
      className="flex items-start gap-3 py-3 border-b last:border-b-0"
      style={{ borderColor: "var(--ta-border)" }}
    >
      <span
        className="mt-1.5 rounded-full flex-shrink-0"
        style={{ width: 10, height: 10, background: dot }}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" style={{ color: "var(--ta-primary)" }}>
          {item.label}
        </div>
        {item.detail && (
          <div className="text-xs mt-0.5" style={{ color: "var(--ta-text-secondary)" }}>
            {item.detail}
          </div>
        )}
        {item.managedOfferGapKind && (
          <div
            className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ background: "#fff8ec", color: "#7a4a00", border: "1px solid #f0d99f" }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
            </svg>
            We can help &mdash; see below
          </div>
        )}
      </div>
    </li>
  );
};


/* ---------- Website Check (Sprint 3I) ----------
 *
 * Customer-safe view of the auto-pilot homepage scan. Reads
 * `results.websiteCheck.state` — one of:
 *   pending | chat_present | chat_missing | protected | error | no_website
 *
 * Never exposes HTTP status codes, hcdn / server headers,
 * possiblePlatform, scanner version, raw error reason, or any bot-
 * challenge technical detail. Those diagnostics remain admin-only.
 */
const WebsiteCheckSection = ({ check }) => {
  const state = check?.state || "pending";
  const headline = check?.headline || "Checking how your website engages visitors\u2026";
  const supporting = check?.supportingText || null;

  const iconByState = {
    pending: "\u2022\u2022\u2022",
    chat_present: "\u2713",
    chat_missing: "\u2014",
    protected: "\u26a0",
    error: "\u2014",
    no_website: "\u2014",
  };

  const toneByState = {
    pending: { fg: "var(--ta-text-secondary)", bg: "var(--ta-surface, #f7f5f0)" },
    chat_present: { fg: "var(--ta-success)", bg: "rgba(52, 130, 96, 0.08)" },
    chat_missing: { fg: "var(--ta-text-secondary)", bg: "var(--ta-surface, #f7f5f0)" },
    protected: { fg: "var(--ta-warning, #b45309)", bg: "rgba(180, 83, 9, 0.06)" },
    error: { fg: "var(--ta-text-secondary)", bg: "var(--ta-surface, #f7f5f0)" },
    no_website: { fg: "var(--ta-text-secondary)", bg: "var(--ta-surface, #f7f5f0)" },
  };
  const tone = toneByState[state] || toneByState.pending;

  return (
    <section
      className="mt-8 p-5 sm:p-6 rounded-2xl bg-white border"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="results-website-check-section"
    >
      <h2
        className="font-display text-base sm:text-lg font-semibold mb-3"
        style={{ color: "var(--ta-primary)" }}
      >
        Website Check
      </h2>
      <div
        className="flex items-start gap-3 rounded-xl p-4"
        style={{ background: tone.bg }}
        data-testid={`results-website-check-state-${state}`}
      >
        <div
          aria-hidden="true"
          className="flex-shrink-0 w-8 h-8 rounded-full inline-flex items-center justify-center text-sm font-semibold"
          style={{ color: tone.fg, background: "white" }}
        >
          {iconByState[state]}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="font-semibold text-sm sm:text-base leading-snug"
            style={{ color: "var(--ta-primary)" }}
            data-testid="results-website-check-headline"
          >
            {headline}
          </p>
          {supporting && (
            <p
              className="mt-1 text-sm leading-relaxed"
              style={{ color: "var(--ta-text-secondary)" }}
              data-testid="results-website-check-supporting"
            >
              {supporting}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};


/* ---------- Top Findings ("What we found so far") — Sprint 3J ----------
 *
 * Renders up to 3 items drawn from persisted data on the backend.
 * Positives first. Unknown / inconclusive results are already filtered
 * server-side — this component just renders what it's given. If the
 * list is empty (a brand-new business with no strong signals yet) the
 * card renders a calm placeholder instead of hiding — reinforcing
 * "we've already started."
 */
const TopFindingsSection = ({ findings }) => {
  const items = Array.isArray(findings) ? findings : [];
  return (
    <section
      className="mt-8 p-5 sm:p-6 rounded-2xl bg-white border"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="results-top-findings-section"
    >
      <h2
        className="font-display text-base sm:text-lg font-semibold mb-3"
        style={{ color: "var(--ta-primary)" }}
      >
        What we found so far
      </h2>
      {items.length === 0 ? (
        <p
          className="text-sm leading-relaxed"
          style={{ color: "var(--ta-text-secondary)" }}
          data-testid="results-top-findings-empty"
        >
          We&apos;re still gathering signal on your business. Once the background checks finish, the strongest positives and opportunities will show up here.
        </p>
      ) : (
        <ul className="space-y-2.5" data-testid="results-top-findings-list">
          {items.map((it, idx) => {
            const isPositive = it.tone === "positive";
            const glyph = isPositive ? "\u2713" : "\u2192";
            const glyphColor = isPositive
              ? "var(--ta-success)"
              : "var(--ta-warning, #b45309)";
            return (
              <li
                key={idx}
                className="flex items-start gap-3"
                data-testid={`results-top-finding-${it.tone}`}
              >
                <span
                  aria-hidden="true"
                  className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 text-sm font-bold"
                  style={{ color: glyphColor }}
                >
                  {glyph}
                </span>
                <span
                  className="text-sm leading-relaxed"
                  style={{ color: "var(--ta-primary)" }}
                >
                  {it.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};


/* ---------- Your First Monday Brief — Sprint 3J ----------
 *
 * Reads the earliest scheduled brief_deliveries row for the business
 * and displays the local-time delivery information. Falls back to
 * "preparing" copy when the background pipeline hasn't yet scheduled
 * a delivery.
 */
const FirstBriefSection = ({ firstBrief }) => {
  const status = firstBrief?.status || "preparing";
  const localTime = firstBrief?.scheduledForLocal;
  const recipient = firstBrief?.recipientEmail;

  return (
    <section
      className="mt-8 p-5 sm:p-6 rounded-2xl"
      style={{
        background: "var(--ta-primary)",
        color: "white",
      }}
      data-testid="results-first-brief-section"
    >
      <div
        className="text-xs uppercase tracking-widest font-semibold mb-2 opacity-80"
        data-testid="results-first-brief-eyebrow"
      >
        YOUR FIRST MONDAY BRIEF
      </div>
      {status === "scheduled" && localTime ? (
        <>
          <p className="text-lg sm:text-xl font-semibold leading-snug">
            Your first Monday Brief is scheduled for:
          </p>
          <p
            className="mt-2 text-2xl sm:text-3xl font-display font-semibold tracking-tight"
            data-testid="results-first-brief-local-time"
          >
            {localTime}
          </p>
          {recipient && (
            <p
              className="mt-4 text-sm opacity-90 leading-relaxed"
              data-testid="results-first-brief-recipient"
            >
              We&apos;ll send it to:{" "}
              <span className="font-semibold">{recipient}</span>
            </p>
          )}
          <p
            className="mt-3 text-sm opacity-90 leading-relaxed"
            data-testid="results-first-brief-no-login"
          >
            No login required. Your Monday Brief comes directly to your inbox.
          </p>
        </>
      ) : (
        <>
          <p
            className="text-lg sm:text-xl font-semibold leading-snug"
            data-testid="results-first-brief-preparing"
          >
            We&apos;re preparing your first Monday Brief now.
          </p>
          {recipient && (
            <p
              className="mt-3 text-sm opacity-90 leading-relaxed"
              data-testid="results-first-brief-recipient"
            >
              We&apos;ll send it to:{" "}
              <span className="font-semibold">{recipient}</span>
            </p>
          )}
          <p
            className="mt-3 text-sm opacity-90 leading-relaxed"
            data-testid="results-first-brief-no-login"
          >
            No login required. Your Monday Brief comes directly to your inbox.
          </p>
        </>
      )}
    </section>
  );
};



/* ---------- AI Discovery Check ---------- */
const AiVisibilitySection = ({ state, snapshot, onRun, businessName, businessId }) => {
  const navigate = useNavigate();
  const complete = snapshot?.status === "complete" || snapshot?.status === "partial";
  const score = snapshot?.visibilityScore?.total;
  const learnMoreHref = businessId
    ? `/offers/ai-visibility/${businessId}`
    : "/offers/ai-visibility";
  return (
    <section
      data-testid="results-ai-visibility"
      className="rounded-2xl p-6"
      style={{ background: "var(--ta-surface)", border: "1px solid var(--ta-border)" }}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: "var(--ta-primary)" }}>
            AI Discovery Check
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }}>
            What ChatGPT, Gemini, and Claude currently understand about{" "}
            {businessName || "your business"}.
          </p>
        </div>
        {/* Sprint 3K — customer-facing 0-of-100 score removed.
            Inconclusive / not-enough-information results must never
            collapse to a numeric score that implies failure. */}
      </div>

      {state === "idle" && !complete && (
        <button
          data-testid="ai-visibility-run"
          onClick={onRun}
          className="mt-5 px-5 py-2.5 rounded-full text-sm font-semibold"
          style={{ background: "var(--ta-primary)", color: "white" }}
        >
          Run my AI Discovery Check
        </button>
      )}

      {state === "running" && <AiSnapshotProgress />}

      {state === "error" && (
        <div className="mt-5 text-sm" style={{ color: "#a4272b" }}>
          Something interrupted your snapshot. You can try again below.
          <div className="mt-3">
            <button
              data-testid="ai-visibility-retry"
              onClick={onRun}
              className="px-4 py-2 rounded-full text-sm font-semibold"
              style={{ background: "var(--ta-primary)", color: "white" }}
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {complete && snapshot?.probes && (
        <ProviderGrid snapshot={snapshot} />
      )}

      {complete && (
        <div className="mt-5">
          <p className="text-sm" style={{ color: "var(--ta-primary)" }} data-testid="ai-discovery-strongest">
            <span className="font-semibold">Strongest positive: </span>
            {snapshot?.summary?.strongestPositive
              || "Not enough reliable information was found to identify a clear positive yet."}
          </p>
          <p className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }} data-testid="ai-discovery-opportunity">
            <span className="font-semibold">Most important opportunity: </span>
            {snapshot?.summary?.mostImportantOpportunity
              || "No clear AI discovery issue was identified in this check."}
          </p>
          {snapshot?.completedAt && (
            <p className="mt-3 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
              Scan completed {new Date(snapshot.completedAt).toLocaleDateString()}.
            </p>
          )}
          <p className="mt-2 text-xs italic" style={{ color: "var(--ta-text-secondary)" }}>
            This is a controlled AI discovery check performed on the date shown.
            Results may vary based on the question, location, available sources,
            model, and the person using the consumer application.
          </p>
        </div>
      )}

      <div className="mt-6 pt-6 text-center" style={{ borderTop: "1px solid var(--ta-border)" }}>
        <button
          data-testid="ai-visibility-learn-why"
          onClick={() => navigate(learnMoreHref)}
          className="text-lg sm:text-xl font-semibold underline"
          style={{ color: "var(--ta-primary)" }}
        >
          Get Listed In The AI Engines →
        </button>
      </div>
    </section>
  );
};

/* ---------- AI Snapshot progress indicator ---------- */
const AI_PROGRESS_PHASES = [
  { at: 0, text: "Asking ChatGPT what it knows about you…" },
  { at: 8, text: "Asking Gemini what it knows about you…" },
  { at: 16, text: "Asking Claude what it knows about you…" },
  { at: 26, text: "Scoring what each model got right…" },
  { at: 35, text: "Almost done — writing your summary…" },
];
const AI_EST_SECONDS = 45; // realistic ceiling before we cap the bar

const AiSnapshotProgress = () => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => clearInterval(t);
  }, []);
  const pct = Math.min(96, Math.round((elapsed / AI_EST_SECONDS) * 100));
  const phase = [...AI_PROGRESS_PHASES].reverse().find((p) => elapsed >= p.at) || AI_PROGRESS_PHASES[0];
  return (
    <div className="mt-5" data-testid="ai-visibility-progress">
      <div className="text-sm mb-2" style={{ color: "var(--ta-primary)" }}>
        {phase.text}
      </div>
      <div
        className="w-full h-2 rounded-full overflow-hidden"
        style={{ background: "var(--ta-surface-alt)" }}
      >
        <div
          className="h-full transition-[width] duration-500 ease-out"
          style={{
            width: `${pct}%`,
            background: "var(--ta-primary)",
          }}
        />
      </div>
      <div className="mt-2 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
        {elapsed}s elapsed &middot; typically 10&ndash;40 seconds. Please don&apos;t close this tab.
      </div>
    </div>
  );
};


const ScoreBubble = ({ score }) => {
  if (score == null) return null;
  const tone = score >= 60 ? "#1f7a3a" : score >= 25 ? "#8a5a15" : "#a4272b";
  return (
    <div
      data-testid="ai-visibility-score"
      className="text-center rounded-2xl px-4 py-3 flex-shrink-0"
      style={{ background: "var(--ta-surface-alt)", border: `2px solid ${tone}` }}
    >
      <div className="text-2xl font-semibold" style={{ color: tone }}>
        {Math.round(score)}
      </div>
      <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: "var(--ta-text-secondary)" }}>
        of 100
      </div>
    </div>
  );
};

const PROVIDER_LABELS = { chatgpt: "ChatGPT", gemini: "Gemini", claude: "Claude", perplexity: "Perplexity" };
const NEW_ROSTER = ["chatgpt", "gemini", "claude"];

const _plainYesNo = (v) => {
  switch (v) {
    case "yes": return "Yes";
    case "no": return "No";
    case "partly": return "Partly";
    case "partly_accurate": return "Partly";
    case "accurate": return "Yes";
    case "inaccurate": return "No";
    case "strong": return "Yes";
    case "some": return "Some";
    case "limited": return "Limited";
    case "none_confirmed": return "No";
    case "inconclusive": return "Not enough information";
    case "unknown": return "Not enough information";
    case "high": return "High";
    case "medium": return "Medium";
    case "low": return "Low";
    default: return "Not enough information";
  }
};
const _toneFor = (v) => {
  if (v === "yes" || v === "strong" || v === "accurate") return "#1f7a3a";
  if (v === "partly" || v === "partly_accurate" || v === "some") return "#8a5a15";
  if (v === "no" || v === "inaccurate" || v === "none_confirmed") return "#a4272b";
  return "var(--ta-text-secondary)";
};

const ProviderGrid = ({ snapshot }) => {
  const rows = snapshot?.providers || snapshot?.probes || [];
  // Detect the new normalized schema by looking for `businessRecognized`.
  const isNewSchema = rows.some((r) => r && (r.businessRecognized || r.providerDisplayName));

  if (isNewSchema) {
    // New Sprint 2A schema — one card per enabled provider in the new roster.
    const byProvider = {};
    for (const r of rows) if (r && r.provider) byProvider[r.provider] = r;
    return (
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ai-provider-grid">
        {NEW_ROSTER.map((prov) => {
          const r = byProvider[prov];
          return (
            <div
              key={prov}
              data-testid={`ai-provider-${prov}`}
              className="rounded-xl p-4"
              style={{ background: "var(--ta-surface-alt)", border: "1px solid var(--ta-border)" }}
            >
              <div className="text-sm font-semibold mb-3" style={{ color: "var(--ta-primary)" }}>
                {PROVIDER_LABELS[prov]}
              </div>
              {!r || r.status === "error" || r.status === "parse_error" || r.status === "timeout" ? (
                <div className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
                  Unavailable — we couldn&apos;t get a clear answer this time.
                </div>
              ) : (
                <>
                  {/* Sprint 3K — customer-safe structured summary only.
                      Long raw provider narratives that read like model
                      diagnostics are hidden on the Results page. */}
                  <NormalizedRow label="Recognized your business" value={r.businessRecognized} />
                  <NormalizedRow label="Understood your services" value={r.servicesUnderstood} />
                  <NormalizedRow label="Website found" value={r.websiteFound} />
                  <NormalizedRow label="Would consider recommending" value={r.recommended} />
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Legacy display: historical snapshots kept for read-only compatibility.
  return <LegacyProviderGrid probes={rows} />;
};

const NormalizedRow = ({ label, value }) => (
  <div className="mb-2 last:mb-0 flex justify-between text-xs">
    <span style={{ color: "var(--ta-text-secondary)" }}>{label}</span>
    <span className="font-medium" style={{ color: _toneFor(value) }}>{_plainYesNo(value)}</span>
  </div>
);

/* Legacy 6-probe display, retained so historical Perplexity/6-probe
   snapshots in the database still render on the Results page. */
const LegacyProviderGrid = ({ probes }) => {
  const byProvider = useMemo(() => {
    const g = { chatgpt: [], gemini: [], perplexity: [] };
    for (const p of probes || []) if (g[p.provider]) g[p.provider].push(p);
    return g;
  }, [probes]);
  return (
    <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ai-provider-grid-legacy">
      {["chatgpt", "gemini", "perplexity"].map((prov) => {
        const direct = byProvider[prov].find((p) => p.probe === "direct");
        const reco = byProvider[prov].find((p) => p.probe === "recommendation");
        return (
          <div
            key={prov}
            data-testid={`ai-provider-${prov}`}
            className="rounded-xl p-4"
            style={{ background: "var(--ta-surface-alt)", border: "1px solid var(--ta-border)" }}
          >
            <div className="text-sm font-semibold mb-3" style={{ color: "var(--ta-primary)" }}>
              {PROVIDER_LABELS[prov]}
            </div>
            <ProbeRow label="Knows you?" probe={direct} kind="direct" />
            <ProbeRow label="Recommended?" probe={reco} kind="recommendation" />
          </div>
        );
      })}
    </div>
  );
};

const ProbeRow = ({ label, probe, kind }) => {
  let value = "—";
  let tone = "var(--ta-text-secondary)";
  if (!probe) {
    value = "—";
  } else if (probe.status === "error") {
    value = "Not available";
    tone = "#a4272b";
  } else if (probe.status === "parse_error") {
    value = "Unclear response";
    tone = "#8a5a15";
  } else if (probe.parsed) {
    if (kind === "direct") {
      const knows = probe.parsed.knows_business;
      if (knows) {
        const conf = probe.parsed.confidence || "medium";
        value = `Yes · ${conf}`;
        tone = "#1f7a3a";
      } else {
        value = "No";
        tone = "#a4272b";
      }
    } else if (kind === "recommendation") {
      const named = probe.parsed.named_business;
      const pos = probe.parsed.position_in_list;
      if (named && pos) {
        value = `Yes · #${pos} in list`;
        tone = "#1f7a3a";
      } else if (named) {
        value = "Mentioned";
        tone = "#1f7a3a";
      } else {
        value = "Not named";
        tone = "#a4272b";
      }
    }
  }
  const quote =
    probe?.parsed?.exact_quote ||
    probe?.parsed?.why_named_or_missing ||
    (probe?.status === "error" ? probe?.errorMessage : "");
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex justify-between text-xs" style={{ color: "var(--ta-text-secondary)" }}>
        <span>{label}</span>
        <span className="font-medium" style={{ color: tone }}>{value}</span>
      </div>
      {quote && (
        <p className="mt-1 text-xs italic leading-snug" style={{ color: "var(--ta-text-secondary)" }}>
          &ldquo;{quote}&rdquo;
        </p>
      )}
    </div>
  );
};

/* ---------- Social Presence ---------- */
const SocialPresenceSection = ({ report }) => {
  // Sprint 3K — compact 3-row summary for the initial Results page.
  // The heavier connect / candidate-picker / meta-connect flows live
  // behind Admin surfaces and can be surfaced later from the ongoing
  // product; the "wow moment" page keeps the noise down.
  if (!report?.channels?.length) return null;
  const label = {
    facebook: "Facebook",
    instagram: "Instagram",
    google_business_profile: "Google Business Profile",
  };
  const compactStatus = (c) => {
    // Only expose short, calm phrases. Never surface raw search URLs,
    // API errors, or long candidate-picker copy.
    if (c.urlProvided && (c.confidence === "confirmed" || c.status === "connected"
        || c.status === "confirmed" || c.confidence === "high")) {
      return { text: "Found", tone: "var(--ta-success)" };
    }
    if (c.urlProvided) {
      return { text: "Needs confirmation", tone: "var(--ta-warning, #b45309)" };
    }
    return { text: "Not detected yet", tone: "var(--ta-text-secondary)" };
  };
  return (
    <section
      data-testid="results-social-presence"
      className="mt-8 rounded-2xl p-6"
      style={{ background: "var(--ta-surface)", border: "1px solid var(--ta-border)" }}
    >
      <div
        className="text-xs uppercase tracking-widest font-semibold mb-3"
        style={{ color: "var(--ta-secondary)" }}
      >
        SOCIAL PRESENCE
      </div>
      <ul className="space-y-2.5" data-testid="results-social-presence-compact-list">
        {report.channels.map((c) => {
          const s = compactStatus(c);
          return (
            <li
              key={c.channel}
              className="flex items-center justify-between gap-3"
              data-testid={`social-compact-${c.channel}`}
            >
              <span
                className="text-sm font-semibold"
                style={{ color: "var(--ta-primary)" }}
              >
                {label[c.channel] || c.channel}
              </span>
              <span
                className="text-sm font-medium"
                style={{ color: s.tone }}
                data-testid={`social-compact-status-${c.channel}`}
              >
                {s.text}
              </span>
            </li>
          );
        })}
      </ul>
      <p
        className="mt-4 text-xs leading-relaxed"
        style={{ color: "var(--ta-text-secondary)" }}
      >
        We&apos;ll keep checking your online presence and include useful changes in your Monday Brief.
      </p>
    </section>
  );
};

const SocialChannelCard = ({ title, channel, businessId, businessName, city, state, onRefresh }) => {
  const canFinderRun = channel.channel === "facebook" || channel.channel === "instagram";
  const [finderState, setFinderState] = useState("idle"); // idle | searching | choosing | manual | saving
  const [candidates, setCandidates] = useState([]);
  const [looseCandidates, setLooseCandidates] = useState([]);
  const [googleSearchUrl, setGoogleSearchUrl] = useState("");
  const [manualUrl, setManualUrl] = useState("");
  // Meta connect state — only meaningful for the Facebook channel.
  const isFbChannel = channel.channel === "facebook";
  const [meta, setMeta] = useState(null); // {configured, connected, pages: [...]}
  const [metaBusy, setMetaBusy] = useState(false);

  useEffect(() => {
    if (!isFbChannel || !businessId) return;
    let alive = true;
    (async () => {
      try {
        const { metaStatus } = await import("@/lib/api");
        const s = await metaStatus(businessId);
        if (alive) setMeta(s);
      } catch (e) {
        /* silent — Connect UI just stays hidden */
      }
    })();
    return () => { alive = false; };
  }, [isFbChannel, businessId]);

  let statusText;
  let statusTone = "var(--ta-text-secondary)";
  if (!channel.urlProvided) {
    statusText = "No link on file";
    statusTone = "#a4272b";
  } else if (channel.urlReachable === false) {
    statusText = "Not verified";
    statusTone = "#8a5a15";
  } else if (channel.urlReachable === true && !channel.lastVerifiedActivity) {
    statusText = "Live — activity unverified";
    statusTone = "#8a5a15";
  } else if (channel.lastVerifiedActivity) {
    statusText = `Last update: ${channel.lastVerifiedActivity}`;
    statusTone = "#1f7a3a";
  } else {
    statusText = "—";
  }

  const runFinder = async () => {
    setFinderState("searching");
    try {
      const { searchSocialLinks } = await import("@/lib/api");
      const r = await searchSocialLinks(businessId, channel.channel);
      setCandidates(Array.isArray(r?.candidates) ? r.candidates : []);
      setLooseCandidates(Array.isArray(r?.looseCandidates) ? r.looseCandidates : []);
      setGoogleSearchUrl(r?.googleSearchUrl || "");
      setFinderState("choosing");
    } catch (e) {
      setFinderState("manual");
      toast.error("We couldn't search right now — you can paste your link instead.");
    }
  };

  const chooseCandidate = async (url) => {
    setFinderState("saving");
    try {
      const { saveSocialLink } = await import("@/lib/api");
      await saveSocialLink(businessId, channel.channel, url);
      toast.success(`${title} link saved.`);
      setFinderState("idle");
      onRefresh?.();
    } catch (e) {
      setFinderState("choosing");
      toast.error("We couldn't save that. Try another option.");
    }
  };

  const saveManual = async () => {
    if (!manualUrl.trim()) return;
    setFinderState("saving");
    try {
      const { saveSocialLink } = await import("@/lib/api");
      await saveSocialLink(businessId, channel.channel, manualUrl.trim());
      toast.success(`${title} link saved.`);
      setFinderState("idle");
      setManualUrl("");
      onRefresh?.();
    } catch (e) {
      setFinderState("manual");
      toast.error("That doesn't look like a valid link. Try again.");
    }
  };

  return (
    <div
      data-testid={`social-channel-${channel.channel}`}
      className="rounded-xl p-4 flex flex-col"
      style={{ background: "var(--ta-surface-alt)", border: "1px solid var(--ta-border)" }}
    >
      <div className="text-sm font-semibold mb-2" style={{ color: "var(--ta-primary)" }}>
        {title}
      </div>
      <div className="text-xs font-medium mb-1" style={{ color: statusTone }}>
        {statusText}
      </div>
      {channel.note && (
        <p className="text-xs leading-snug" style={{ color: "var(--ta-text-secondary)" }}>
          {channel.note}
        </p>
      )}

      {/* In-app finder — only for FB/IG when no URL on file */}
      {canFinderRun && !channel.urlProvided && (
        <div className="mt-3">
          {finderState === "idle" && (
            <button
              data-testid={`social-find-${channel.channel}`}
              onClick={runFinder}
              className="w-full px-3 py-2 rounded-full text-xs font-semibold"
              style={{ background: "var(--ta-primary)", color: "white" }}
            >
              Find my {title} page
            </button>
          )}
          {finderState === "searching" && (
            <div className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
              Searching for your {title} page…
            </div>
          )}
          {finderState === "choosing" && (
            <div>
              {candidates.length > 0 && (
                <>
                  <div className="text-xs font-semibold mb-2" style={{ color: "var(--ta-primary)" }}>
                    Which of these is yours?
                  </div>
                  <div className="space-y-1.5">
                    {candidates.map((c, i) => (
                      <button
                        key={`s-${i}`}
                        data-testid={`social-candidate-${channel.channel}-${i}`}
                        onClick={() => chooseCandidate(c.url)}
                        className="w-full text-left px-3 py-2 rounded-lg text-xs"
                        style={{ background: "white", border: "1px solid var(--ta-border)" }}
                      >
                        <div className="font-semibold truncate" style={{ color: "var(--ta-primary)" }}>
                          {c.title || c.url}
                        </div>
                        <div className="text-[10px] truncate" style={{ color: "var(--ta-text-secondary)" }}>
                          {c.url}
                        </div>
                        {c.snippet && (
                          <div className="text-[10px] mt-0.5 line-clamp-2" style={{ color: "var(--ta-text-secondary)" }}>
                            {c.snippet}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {candidates.length === 0 && (
                <div
                  data-testid={`social-absence-banner-${channel.channel}`}
                  className="rounded-lg p-3 mb-2"
                  style={{
                    background: "#fef4e6",
                    border: "1px solid #eecfa0",
                    color: "#7a4a10",
                  }}
                >
                  <div className="text-xs font-semibold mb-1">
                    We couldn&apos;t find a {title} page for {businessName || "your business"}
                    {city ? ` in ${city}` : ""}.
                  </div>
                  <div className="text-xs leading-snug">
                    That&apos;s a real gap. Most local customers check {title} before they call &mdash;
                    and AI models cite social profiles when they recommend businesses.
                    See the Social Media Management offer below if you&apos;d like us to handle it.
                  </div>
                </div>
              )}

              {candidates.length === 0 && looseCandidates.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-semibold mb-2" style={{ color: "var(--ta-primary)" }}>
                    Other {title} pages nearby &mdash; probably not yours, but see how many other local businesses are showing up:
                  </div>
                  <div className="space-y-1.5">
                    {looseCandidates.map((c, i) => (
                      <button
                        key={`l-${i}`}
                        data-testid={`social-loose-candidate-${channel.channel}-${i}`}
                        onClick={() => chooseCandidate(c.url)}
                        className="w-full text-left px-3 py-2 rounded-lg text-xs"
                        style={{ background: "white", border: "1px dashed var(--ta-border)" }}
                      >
                        <div className="font-semibold truncate" style={{ color: "var(--ta-primary)" }}>
                          {c.title || c.url}
                        </div>
                        <div className="text-[10px] truncate" style={{ color: "var(--ta-text-secondary)" }}>
                          {c.url}
                        </div>
                        {c.snippet && (
                          <div className="text-[10px] mt-0.5 line-clamp-2" style={{ color: "var(--ta-text-secondary)" }}>
                            {c.snippet}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button
                data-testid={`social-manual-${channel.channel}`}
                onClick={() => setFinderState("manual")}
                className="mt-2 w-full text-left px-3 py-2 rounded-lg text-xs italic"
                style={{ color: "var(--ta-text-secondary)" }}
              >
                {candidates.length > 0
                  ? "None of these — I'll paste it myself"
                  : "I do have a page — I'll paste the link"}
              </button>

              {googleSearchUrl && (
                <a
                  data-testid={`social-google-fallback-${channel.channel}`}
                  href={googleSearchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 block w-full text-center px-3 py-2 rounded-lg text-xs font-semibold"
                  style={{
                    background: "white",
                    border: "1px solid var(--ta-border)",
                    color: "var(--ta-primary)",
                  }}
                >
                  Search Google for my {title} page →
                </a>
              )}
            </div>
          )}
          {finderState === "manual" && (
            <div>
              <input
                data-testid={`social-manual-input-${channel.channel}`}
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                placeholder={
                  channel.channel === "facebook"
                    ? "facebook.com/yourpage"
                    : "@yourhandle or instagram.com/yourhandle"
                }
                className="w-full px-3 py-2 rounded-lg text-xs bg-white"
                style={{ border: "1px solid var(--ta-border)", color: "var(--ta-primary)" }}
              />
              <button
                data-testid={`social-manual-save-${channel.channel}`}
                onClick={saveManual}
                disabled={!manualUrl.trim()}
                className="mt-2 w-full px-3 py-2 rounded-full text-xs font-semibold disabled:opacity-50"
                style={{ background: "var(--ta-primary)", color: "white" }}
              >
                Save {title} link
              </button>
            </div>
          )}
          {finderState === "saving" && (
            <div className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
              Saving…
            </div>
          )}
        </div>
      )}

      {/* Meta / Facebook Pages connect — verified activity via OAuth */}
      {isFbChannel && meta?.configured && (
        <MetaConnectBlock
          meta={meta}
          setMeta={setMeta}
          businessId={businessId}
          metaBusy={metaBusy}
          setMetaBusy={setMetaBusy}
        />
      )}

      {/* Managed-offer nudge when nothing on file — SMM handles it for them */}
      {isFbChannel && !channel.urlProvided && (
        <a
          data-testid={`social-managed-offer-nudge-facebook`}
          href="#service-offers"
          onClick={(e) => {
            const el = document.getElementById("service-offers");
            if (el) {
              e.preventDefault();
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }}
          className="mt-3 block text-center text-xs italic underline"
          style={{ color: "var(--ta-text-secondary)" }}
        >
          Or let us handle Facebook for you →
        </a>
      )}
    </div>
  );
};

/* ---------- Meta Connect block ---------- */
const MetaConnectBlock = ({ meta, setMeta, businessId, metaBusy, setMetaBusy }) => {
  const onConnect = async () => {
    if (metaBusy) return;
    setMetaBusy(true);
    try {
      const { metaOauthStart } = await import("@/lib/api");
      const r = await metaOauthStart(businessId, window.location.pathname);
      if (r?.url) {
        window.location.href = r.url;
      } else {
        toast.error("We couldn't start the Facebook connect flow.");
        setMetaBusy(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "We couldn't start the Facebook connect flow.";
      toast.error(msg);
      setMetaBusy(false);
    }
  };

  const onDisconnect = async () => {
    if (metaBusy) return;
    if (!window.confirm("Disconnect Facebook from this business? Your posts and follower data will stop refreshing.")) return;
    setMetaBusy(true);
    try {
      const { metaDisconnect, metaStatus } = await import("@/lib/api");
      await metaDisconnect(businessId);
      const s = await metaStatus(businessId);
      setMeta(s);
      toast.success("Facebook disconnected.");
    } catch (e) {
      toast.error("We couldn't disconnect. Try again.");
    } finally {
      setMetaBusy(false);
    }
  };

  if (!meta?.connected) {
    return (
      <div
        data-testid="meta-connect-cta"
        className="mt-4 p-3 rounded-lg"
        style={{ background: "#f3f6fb", border: "1px solid #d0dce8" }}
      >
        <div className="text-xs font-semibold mb-1" style={{ color: "#1c2b3a" }}>
          Want us to actually see your Facebook activity?
        </div>
        <div className="text-xs leading-snug mb-2" style={{ color: "#48607a" }}>
          Facebook won&apos;t show us your page data without your permission. Connect
          your account and we&apos;ll see your posts, followers, and last activity date
          — no more &quot;activity unverified&quot; guesswork.
        </div>
        <button
          data-testid="meta-connect-btn"
          onClick={onConnect}
          disabled={metaBusy}
          className="w-full px-3 py-2 rounded-full text-xs font-semibold disabled:opacity-50"
          style={{ background: "#1877f2", color: "white" }}
        >
          {metaBusy ? "Opening Facebook…" : "Connect Facebook"}
        </button>
      </div>
    );
  }

  // Connected state — show the first page's live data.
  const pages = meta.pages || [];
  const page = pages[0];
  // A connected page with no follower/about signal means we only got
  // `pages_show_list` back — the deeper reads are behind Meta's
  // Advanced Access which the operator hasn't enabled yet.
  const hasDeepData = page && (
    (typeof page.followers === "number" && page.followers > 0) ||
    (page.about && page.about.length > 0) ||
    page.lastPostAt
  );
  return (
    <div
      data-testid="meta-connected-card"
      className="mt-4 p-3 rounded-lg"
      style={{ background: "#eef7ee", border: "1px solid #b4d9b6" }}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold" style={{ color: "#1c4322" }}>
          Facebook connected
        </div>
        <button
          data-testid="meta-disconnect-btn"
          onClick={onDisconnect}
          disabled={metaBusy}
          className="text-[10px] underline disabled:opacity-50"
          style={{ color: "#48607a" }}
        >
          Disconnect
        </button>
      </div>
      {page ? (
        <div className="text-xs" style={{ color: "#1c4322" }}>
          <div className="font-semibold">{page.pageName}</div>

          {hasDeepData ? (
            <div className="mt-1">
              {page.followers != null && page.followers > 0
                ? `${page.followers.toLocaleString()} followers`
                : "Followers: —"}
              {page.lastPostAt && (
                <>
                  {" · "}Last post {new Date(page.lastPostAt).toLocaleDateString()}
                </>
              )}
            </div>
          ) : (
            <div
              className="mt-2 p-2 rounded"
              style={{ background: "white", border: "1px dashed #b4d9b6" }}
              data-testid="meta-limited-scope-note"
            >
              <div className="font-semibold mb-1" style={{ color: "#1c4322" }}>
                What&apos;s happening now
              </div>
              <ul className="list-disc list-inside space-y-1" style={{ color: "#3a5541" }}>
                <li>We have your Page name and permission to see your Page list.</li>
                <li>
                  Follower count, About, and posts require Meta&apos;s{" "}
                  <em>pages_read_engagement</em> and <em>pages_read_user_content</em>{" "}
                  permissions. Add them under Meta App → Use cases →{" "}
                  <em>Manage everything on your Page</em> → Customize → Permissions.
                  Standard Access is granted automatically once you add them — no
                  further approval needed for app admins/testers.
                </li>
                <li>
                  Once added, set{" "}
                  <code>META_OAUTH_SCOPES=&quot;pages_show_list,pages_read_engagement,pages_read_user_content&quot;</code>
                  {" "}in production secrets, redeploy, and reconnect &mdash; then
                  this card fills in with real activity.
                </li>
              </ul>
            </div>
          )}

          {page.about && (
            <div className="mt-1 text-[10px]" style={{ color: "#48607a" }}>
              {page.about}
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs italic" style={{ color: "#48607a" }}>
          We didn&apos;t find any Pages on your Facebook account. If you manage a Page,
          check that Facebook granted us access to it during connect.
        </div>
      )}
    </div>
  );
};

/* ---------- Service Offers ---------- */
const ServiceOffers = ({ offers }) => {
  if (!offers?.length) return null;
  return (
    <section
      id="service-offers"
      data-testid="results-service-offers"
      className="rounded-2xl p-6"
      style={{ background: "var(--ta-accent-soft, #fff8ec)", border: "1px solid var(--ta-border)" }}
    >
      <h2 className="text-lg font-semibold mb-4" style={{ color: "var(--ta-primary)" }}>
        Where we can help
      </h2>
      <div className="space-y-4">
        {offers.map((o, i) => (
          <div
            key={i}
            data-testid={`service-offer-${o.gap_kind}`}
            className="rounded-xl p-4"
            style={{ background: "white", border: "1px solid var(--ta-border)" }}
          >
            <div className="text-sm font-semibold" style={{ color: "var(--ta-primary)" }}>
              {o.problem_headline}
            </div>
            {o.why && (
              <p className="mt-1 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
                {o.why}
              </p>
            )}
            <div className="mt-3 text-sm" style={{ color: "var(--ta-primary)" }}>
              {o.solution_headline}
            </div>
            <p className="mt-1 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
              {o.solution_detail}
            </p>

            {/* Standard YouTube-embed 16:9 slot. Shows a "video coming
                soon" card until the owner drops a real YouTube URL in
                the Service Offers config; swaps to a real iframe as soon
                as they do. Size matches a live YouTube embed so no
                layout shift when the video URL is set. */}
            {o.video_url && (
              <div
                className="mt-4 relative w-full rounded-lg overflow-hidden"
                style={{
                  paddingTop: "56.25%",
                  background: "#111",
                  border: "1px solid var(--ta-border)",
                }}
              >
                {o.video_url === "PLACEHOLDER_VIDEO" || !o.video_url.startsWith("http") ? (
                  <div
                    data-testid={`offer-video-placeholder-${o.gap_kind}`}
                    className="absolute inset-0 flex flex-col items-center justify-center text-center px-4"
                    style={{ color: "#eaeaea" }}
                  >
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
                      style={{ background: "rgba(255,255,255,0.08)" }}
                    >
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    <div className="text-sm font-semibold">Video coming soon</div>
                    <div className="text-xs mt-1 opacity-70">
                      A short explainer from Your Monday Brief goes here.
                    </div>
                  </div>
                ) : (
                  <iframe
                    data-testid={`offer-video-${o.gap_kind}`}
                    className="absolute inset-0 w-full h-full"
                    src={o.video_url}
                    title={`${o.solution_headline} — video`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <a
                data-testid={`offer-cta-${o.gap_kind}`}
                href={o.cta_url}
                target="_blank"
                rel="noreferrer"
                className="px-5 py-2.5 rounded-full text-sm font-semibold"
                style={{ background: "var(--ta-primary)", color: "white" }}
              >
                {o.cta_label}
              </a>
              <span className="text-sm font-medium" style={{ color: "var(--ta-primary)" }}>
                {o.price_display}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
